import React, { Fragment, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import {
  MDBBtn, MDBCard, MDBCardBody, MDBCardHeader,
  MDBCol, MDBContainer, MDBInput, MDBRow,
} from "mdb-react-ui-kit";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  LineElement, PointElement, Filler, Tooltip, Legend,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import agent from "../../../agent";
import { LOADER_SHOW } from "../../../constants/actionTypes";
import CalendarPicker from "./CalendarPicker";

ChartJS.register(
  CategoryScale, LinearScale, BarElement,
  LineElement, PointElement, Filler, Tooltip, Legend
);

const REPORT_CONFIG = {
  "user-acquisition": {
    title: "User Acquisition",
    endpoint: "gaUserAcquisition",
    primaryMetricIndex: 0,
    chartLabel: "Total Users",
  },
  events: {
    title: "Events",
    endpoint: "gaEvents",
    primaryMetricIndex: 0,
    chartLabel: "Event Count",
  },
  "landing-pages": {
    title: "Landing Pages",
    endpoint: "gaLandingPages",
    primaryMetricIndex: 0,
    chartLabel: "Sessions",
  },
};

const mapDispatchToProps = (dispatch) => ({
  callLoaderShow: (type) => dispatch({ type: LOADER_SHOW, payload: { type } }),
});

function toISODate(input) {
  if (!input) return "";
  return String(input).slice(0, 10);
}
function formatToGA4Date(dateStr) {
  if (!dateStr) return "";
  const s = String(dateStr).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) { const [dd,mm,yyyy]=s.split("-"); return `${yyyy}-${mm}-${dd}`; }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) { const [dd,mm,yyyy]=s.split("/"); return `${yyyy}-${mm}-${dd}`; }
  return s;
}
function parseWebsites(raw) {
  if (!raw) return [];
  try { const p=JSON.parse(String(raw)); return Array.isArray(p)?p.filter(Boolean):[]; } catch { return []; }
}
function formatNumber(v) {
  const n=Number(v||0); return Number.isNaN(n)?v||"0":n.toLocaleString();
}
function formatPercent(value) { return `${(Number(value||0)*100).toFixed(2)}%`; }
function formatDurationSeconds(value) {
  const sec=Math.max(0,Math.round(Number(value||0)));
  return `${Math.floor(sec/60)}m ${sec%60}s`;
}
function formatDecimal(value, decimals=2) { return Number(value||0).toFixed(decimals); }
function getMetricValue(row,idx) { return Number(row?.metricValues?.[idx]?.value||0); }
function getDimensionValue(row,idx=0) { return row?.dimensionValues?.[idx]?.value||"-"; }
function buildRowLimitOptions(total) {
  if(total===0) return ["all"];
  const opts=[10,25,50,100].filter(n=>n<total);
  if(!opts.includes(total)) opts.push(total);
  return [...opts,"all"];
}
function formatChartDateLabel(d) {
  if(!d||d.length!==8) return d;
  return `${d.slice(6,8)}/${d.slice(4,6)}`;
}
function calculateCompareDateRange(startDate,endDate) {
  const start=new Date(startDate), end=new Date(endDate);
  const daysDiff=Math.floor((end-start)/86400000);
  const compareEnd=new Date(start.getTime()-86400000);
  const compareStart=new Date(compareEnd.getTime()-daysDiff*86400000);
  return [compareStart.toISOString().slice(0,10), compareEnd.toISOString().slice(0,10)];
}
function calcPctChange(current,previous) {
  const c=Number(current||0), p=Number(previous||0);
  if(p===0) return c===0?0:100;
  return ((c-p)/p)*100;
}
function getChangeColor(pct) { return pct>0?"#059669":pct<0?"#dc2626":"#6b7280"; }
function renderPctChange(current,previous) {
  const pct=calcPctChange(current,previous);
  return (
    <span style={{color:getChangeColor(pct),fontWeight:600}}>
      {pct>=0?"+":""}{pct.toFixed(2)}%
    </span>
  );
}

// ── Shared line chart palette ─────────────────────────────────────────────────
const PALETTE = ["#3b82f6","#22c55e","#f59e0b","#ef4444","#a855f7","#06b6d4","#ec4899","#84cc16"];

const GoogleReportDetailPage = ({ callLoaderShow }) => {
  const location = useLocation();
  const pathSlug = location.pathname.split("/").pop();
  const reportKey = REPORT_CONFIG[pathSlug] ? pathSlug : "user-acquisition";
  const config = REPORT_CONFIG[reportKey];

  const params = new URLSearchParams(location.search);
  const [websiteKey, setWebsiteKey]         = useState(params.get("websiteKey")||"");
  const [startDate,  setStartDate]           = useState(params.get("startDate")||toISODate(new Date(Date.now()-30*86400000).toISOString()));
  const [endDate,    setEndDate]             = useState(params.get("endDate")||toISODate(new Date().toISOString()));
  const [compareOn,  setCompareOn]           = useState(Boolean(params.get("compareStartDate")&&params.get("compareEndDate")));
  const [compareStartDate, setCompareStartDate] = useState(params.get("compareStartDate")||"");
  const [compareEndDate,   setCompareEndDate]   = useState(params.get("compareEndDate")||"");
  const [search,   setSearch]   = useState("");
  const [rowLimit, setRowLimit] = useState(25);
  const [sortBy,   setSortBy]   = useState(reportKey==="user-acquisition"?"totalUsers":"sessions");
  const [sortDir,  setSortDir]  = useState("desc");
  const [errorMsg, setErrorMsg] = useState(null);
  const [websites, setWebsites] = useState([]);
  const [report,   setReport]   = useState(null);
  const [landingTimeseries,  setLandingTimeseries]  = useState(null);
  const [userAcqTimeseries,  setUserAcqTimeseries]  = useState(null);
  const [eventsTimeseries,   setEventsTimeseries]   = useState(null);
  const [showCalendar,        setShowCalendar]        = useState(false);
  const [showCompareCalendar, setShowCompareCalendar] = useState(false);

  useEffect(() => {
    if (compareOn && startDate && endDate) {
      const [cStart,cEnd] = calculateCompareDateRange(startDate,endDate);
      setCompareStartDate(cStart);
      setCompareEndDate(cEnd);
    }
  }, [startDate,endDate,compareOn]);

  useEffect(() => {
    const loadWebsites = async () => {
      try {
        callLoaderShow(true);
        const r = await agent.Auth.getSettings();
        callLoaderShow(false);
        const list = parseWebsites(r?.data?.settings?.google_websites);
        setWebsites(list);
        if (list.length) {
          const urlKey = params.get("websiteKey");
          const valid  = urlKey && list.some(w=>String(w.key)===String(urlKey));
          if (!valid) setWebsiteKey(String(list[0].key||""));
        }
      } catch(err) { callLoaderShow(false); setErrorMsg(err?.message||"Failed to load websites."); }
    };
    loadWebsites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReport = async () => {
    try {
      setErrorMsg(null);
      if (!websiteKey) { setErrorMsg("Please select a website."); return; }
      const ga4Start = formatToGA4Date(startDate);
      const ga4End   = formatToGA4Date(endDate);
      const reqParams = { startDate: ga4Start, endDate: ga4End };
      if (compareOn && compareStartDate && compareEndDate) {
        reqParams.compareStartDate = formatToGA4Date(compareStartDate);
        reqParams.compareEndDate   = formatToGA4Date(compareEndDate);
      }
      callLoaderShow(true);
      const res = await agent.Google[config.endpoint](websiteKey, reqParams);

      let landingTs = null, userAcqTs = null;
      if (reportKey === "landing-pages") {
        const tsRes = await agent.Google.gaLandingPagesTimeseries(websiteKey,
          { startDate: ga4Start, endDate: ga4End, topN: 5 });
        landingTs = tsRes?.data || tsRes || null;
      }
      if (reportKey === "user-acquisition") {
        const tsRes = await agent.Google.gaUserAcquisitionTimeseries(websiteKey,
          { startDate: ga4Start, endDate: ga4End });
        userAcqTs = tsRes?.data || tsRes || null;
      }
      let eventsTs = null;
      if (reportKey === "events") {
        const tsRes = await agent.Google.gaEventsTimeseries(websiteKey,
          { startDate: ga4Start, endDate: ga4End, topN: 5 });
        eventsTs = tsRes?.data || tsRes || null;
      }

      callLoaderShow(false);
      setReport(res?.data?.report || res?.report || null);
      setLandingTimeseries(landingTs);
      setUserAcqTimeseries(userAcqTs);
      setEventsTimeseries(eventsTs);
      setRowLimit(25);
      setSearch(""); // reset search on new fetch
    } catch(err) { callLoaderShow(false); setErrorMsg(err?.message||"Failed to load report."); }
  };

  useEffect(() => {
    if (websiteKey) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteKey]);

  const totalRows = report?.rows?.length || 0;
  const rowLimitOptions = useMemo(() => buildRowLimitOptions(totalRows), [totalRows]);

  // ── Events rows ───────────────────────────────────────────────────────────
  const eventsRows = useMemo(() => {
    if (reportKey !== "events") return [];
    const mapped = (report?.rows||[]).map(row => {
      const eventCount        = Number(row?.metricValues?.[0]?.value||0);
      const totalUsers        = Number(row?.metricValues?.[1]?.value||0);
      const eventCountPerUser = Number(row?.metricValues?.[2]?.value||0);
      const totalRevenue      = Number(row?.metricValues?.[3]?.value||0);
      const cmp               = row?.compareMetricValues||[];
      return {
        eventName: getDimensionValue(row,0),
        eventCount, totalUsers, eventCountPerUser, totalRevenue,
        cmpEventCount:        Number(cmp[0]?.value||0),
        cmpTotalUsers:        Number(cmp[1]?.value||0),
        cmpEventCountPerUser: Number(cmp[2]?.value||0),
        cmpTotalRevenue:      Number(cmp[3]?.value||0),
      };
    });
    const q = search.toLowerCase();
    const filtered = !q ? mapped : mapped.filter(r =>
      String(r.eventName||"").toLowerCase().includes(q) ||
      String(r.eventCount||"").includes(q) ||
      String(r.totalUsers||"").includes(q) ||
      String(Number(r.eventCountPerUser||0).toFixed(2)).includes(q) ||
      String(Number(r.totalRevenue||0).toFixed(2)).includes(q)
    );
    const sorted = [...filtered].sort((a,b) => {
      const av=a[sortBy]??0, bv=b[sortBy]??0;
      if(typeof av==="string"||typeof bv==="string"){ const c=String(av||"").localeCompare(String(bv||"")); return sortDir==="asc"?c:-c; }
      const c=Number(av||0)-Number(bv||0); return sortDir==="asc"?c:-c;
    });
    return rowLimit==="all"?sorted:sorted.slice(0,rowLimit);
  }, [report,reportKey,rowLimit,search,sortBy,sortDir]);

  // ── Events summary ─────────────────────────────────────────────────────────
  const eventsSummary = useMemo(() => {
    if (reportKey !== "events") return {};
    const rawRows=report?.rows||[];
    const ga4Totals=report?.totals?.[0]?.metricValues||[];
    const ga4Total=(idx)=>ga4Totals[idx]!=null?Number(ga4Totals[idx].value||0):null;
    const totalEventCount        = ga4Total(0)??rawRows.reduce((s,r)=>s+getMetricValue(r,0),0);
    const totalTotalUsers        = ga4Total(1)??rawRows.reduce((s,r)=>s+getMetricValue(r,1),0);
    const avgEventCountPerUser   = ga4Total(2)??0;
    const totalRevenue           = ga4Total(3)??rawRows.reduce((s,r)=>s+getMetricValue(r,3),0);
    const cmpTotals=report?.compareTotals?.[0]?.metricValues||[];
    const cmpTotal=(idx)=>cmpTotals[idx]!=null?Number(cmpTotals[idx].value||0):0;
    return {
      totalEventCount, totalTotalUsers, avgEventCountPerUser, totalRevenue,
      cmpEventCount:        cmpTotal(0),
      cmpTotalUsers:        cmpTotal(1),
      cmpEventCountPerUser: cmpTotal(2),
      cmpTotalRevenue:      cmpTotal(3),
    };
  }, [report,reportKey]);

  // ── Landing Pages rows ─────────────────────────────────────────────────────
  const landingRows = useMemo(() => {
    if (reportKey !== "landing-pages") return [];
    const mapped = (report?.rows||[]).map(row => {
      const sessions=Number(row?.metricValues?.[0]?.value||0);
      const activeUsers=Number(row?.metricValues?.[1]?.value||0);
      const newUsers=Number(row?.metricValues?.[2]?.value||0);
      const userEngagementDuration=Number(row?.metricValues?.[3]?.value||0);
      const keyEvents=Number(row?.metricValues?.[4]?.value||0);
      const totalRevenue=Number(row?.metricValues?.[5]?.value||0);
      const sessionKeyEventRate=Number(row?.metricValues?.[6]?.value||0);
      const cmp=row?.compareMetricValues||[];
      return {
        landingPage: getDimensionValue(row,0),
        sessions, activeUsers, newUsers, userEngagementDuration, keyEvents, totalRevenue, sessionKeyEventRate,
        cmpSessions:Number(cmp[0]?.value||0), cmpActiveUsers:Number(cmp[1]?.value||0),
        cmpNewUsers:Number(cmp[2]?.value||0), cmpUserEngagementDuration:Number(cmp[3]?.value||0),
        cmpKeyEvents:Number(cmp[4]?.value||0), cmpTotalRevenue:Number(cmp[5]?.value||0),
        cmpSessionKeyEventRate:Number(cmp[6]?.value||0),
      };
    });
    const q = search.toLowerCase();
    const filtered = !q ? mapped : mapped.filter(r =>
      String(r.landingPage||"").toLowerCase().includes(q) ||
      String(r.sessions||"").includes(q) ||
      String(r.activeUsers||"").includes(q) ||
      String(r.newUsers||"").includes(q) ||
      String(r.keyEvents||"").includes(q) ||
      String(Number(r.totalRevenue||0).toFixed(2)).includes(q) ||
      String(Number(r.sessionKeyEventRate||0).toFixed(4)).includes(q)
    );
    const sorted = [...filtered].sort((a,b) => {
      const av=a[sortBy], bv=b[sortBy];
      if(typeof av==="string"||typeof bv==="string") { const c=String(av||"").localeCompare(String(bv||"")); return sortDir==="asc"?c:-c; }
      const c=Number(av||0)-Number(bv||0); return sortDir==="asc"?c:-c;
    });
    return rowLimit==="all"?sorted:sorted.slice(0,rowLimit);
  }, [report,reportKey,rowLimit,search,sortBy,sortDir]);

  // ── User Acquisition rows ──────────────────────────────────────────────────
  const userAcqRows = useMemo(() => {
    if (reportKey !== "user-acquisition") return [];
    const mapped = (report?.rows||[]).map(row => {
      const totalUsers              = Number(row?.metricValues?.[0]?.value||0);
      const newUsers                = Number(row?.metricValues?.[1]?.value||0);
      const returningUsers          = Math.max(0, totalUsers - newUsers);
      const userEngagementDuration  = Number(row?.metricValues?.[2]?.value||0);
      const engagedSessions         = Number(row?.metricValues?.[3]?.value||0);
      const eventCount              = Number(row?.metricValues?.[4]?.value||0);
      const activeUsers             = Number(row?.metricValues?.[5]?.value||0);
      const keyEvents               = Number(row?.metricValues?.[6]?.value||0);
      const sessionKeyEventRate     = Number(row?.metricValues?.[7]?.value||0);
      const userKeyEventRate        = activeUsers > 0 ? keyEvents / activeUsers : 0;
      const cmp                     = row?.compareMetricValues||[];
      const cmpTotalUsers           = Number(cmp[0]?.value||0);
      const cmpNewUsers             = Number(cmp[1]?.value||0);
      const cmpReturningUsers       = Math.max(0, cmpTotalUsers - cmpNewUsers);
      const cmpKeyEvents            = Number(cmp[6]?.value||0);
      const cmpActiveUsers          = Number(cmp[5]?.value||0);
      const cmpUserKeyEventRate     = cmpActiveUsers > 0 ? cmpKeyEvents / cmpActiveUsers : 0;
      return {
        channelGroup: getDimensionValue(row,0),
        totalUsers, newUsers, returningUsers, userEngagementDuration,
        engagedSessions, eventCount, activeUsers,
        keyEvents, sessionKeyEventRate, userKeyEventRate,
        cmpTotalUsers, cmpNewUsers, cmpReturningUsers,
        cmpUserEngagementDuration: Number(cmp[2]?.value||0),
        cmpEngagedSessions:        Number(cmp[3]?.value||0),
        cmpEventCount:             Number(cmp[4]?.value||0),
        cmpActiveUsers,
        cmpKeyEvents, cmpUserKeyEventRate,
      };
    });
    const q = search.toLowerCase();
    const filtered = !q ? mapped : mapped.filter(r =>
      String(r.channelGroup||"").toLowerCase().includes(q) ||
      String(r.totalUsers||"").includes(q) ||
      String(r.newUsers||"").includes(q) ||
      String(r.returningUsers||"").includes(q) ||
      String(r.eventCount||"").includes(q) ||
      String(r.keyEvents||"").includes(q) ||
      String(r.activeUsers||"").includes(q)
    );
    const sorted = [...filtered].sort((a,b) => {
      const av=a[sortBy]??0, bv=b[sortBy]??0;
      if(typeof av==="string"||typeof bv==="string") { const c=String(av||"").localeCompare(String(bv||"")); return sortDir==="asc"?c:-c; }
      const c=Number(av||0)-Number(bv||0); return sortDir==="asc"?c:-c;
    });
    return rowLimit==="all"?sorted:sorted.slice(0,rowLimit);
  }, [report,reportKey,rowLimit,search,sortBy,sortDir]);

  const handleSort = (key) => {
    if(sortBy===key){ setSortDir(d=>d==="asc"?"desc":"asc"); return; }
    setSortBy(key); setSortDir("desc");
  };

  // ── Landing Pages summary ──────────────────────────────────────────────────
  const summary = useMemo(() => {
    const rawRows=report?.rows||[];
    const ga4Totals=report?.totals?.[0]?.metricValues||[];
    const ga4Total=(idx)=>ga4Totals[idx]!=null?Number(ga4Totals[idx].value||0):null;
    const totalSessions=ga4Total(0)??rawRows.reduce((s,r)=>s+getMetricValue(r,0),0);
    const totalActiveUsers=ga4Total(1)??rawRows.reduce((s,r)=>s+getMetricValue(r,1),0);
    const totalNewUsers=ga4Total(2)??rawRows.reduce((s,r)=>s+getMetricValue(r,2),0);
    const totalEngagementDuration=ga4Total(3)??rawRows.reduce((s,r)=>s+getMetricValue(r,3),0);
    const totalAvgSessionDuration=totalSessions>0?totalEngagementDuration/totalSessions:0;
    const totalKeyEvents=ga4Total(4)??rawRows.reduce((s,r)=>s+getMetricValue(r,4),0);
    const totalRevenue=ga4Total(5)??rawRows.reduce((s,r)=>s+getMetricValue(r,5),0);
    const totalSessionKeyEventRate=totalSessions>0?totalKeyEvents/totalSessions:0;
    const cmpTotals=report?.compareTotals?.[0]?.metricValues||[];
    const cmpTotal=(idx)=>cmpTotals[idx]!=null?Number(cmpTotals[idx].value||0):0;
    const cmpTotalSessions=cmpTotal(0), cmpTotalActiveUsers=cmpTotal(1), cmpTotalNewUsers=cmpTotal(2);
    const cmpTotalEngagementDuration=cmpTotal(3);
    const cmpTotalAvgSessionDuration=cmpTotal(0)>0?cmpTotalEngagementDuration/cmpTotal(0):0;
    const cmpTotalKeyEvents=cmpTotal(4), cmpTotalRevenue=cmpTotal(5);
    const cmpTotalSessionKeyEventRate=cmpTotal(0)>0?cmpTotal(4)/cmpTotal(0):0;
    return {
      totalPrimary:totalSessions, totalSecondary:totalActiveUsers, totalThird:totalNewUsers,
      totalFourth:totalAvgSessionDuration, totalFifth:totalKeyEvents,
      totalSixth:totalRevenue, totalSeventh:totalSessionKeyEventRate,
      cmpTotalSessions, cmpTotalActiveUsers, cmpTotalNewUsers,
      cmpTotalAvgSessionDuration, cmpTotalKeyEvents, cmpTotalRevenue, cmpTotalSessionKeyEventRate,
    };
  }, [report]);

  // ── User Acquisition summary ───────────────────────────────────────────────
  const userAcqSummary = useMemo(() => {
    if (reportKey !== "user-acquisition") return {};
    const rawRows=report?.rows||[];
    const ga4Totals=report?.totals?.[0]?.metricValues||[];
    const ga4Total=(idx)=>ga4Totals[idx]!=null?Number(ga4Totals[idx].value||0):null;

    const totalTotalUsers         = ga4Total(0)??rawRows.reduce((s,r)=>s+getMetricValue(r,0),0);
    const totalNewUsers           = ga4Total(1)??rawRows.reduce((s,r)=>s+getMetricValue(r,1),0);
    const totalReturningUsers     = Math.max(0, totalTotalUsers - totalNewUsers);
    const totalEngagementDuration = ga4Total(2)??rawRows.reduce((s,r)=>s+getMetricValue(r,2),0);
    const totalEngagedSessions    = ga4Total(3)??rawRows.reduce((s,r)=>s+getMetricValue(r,3),0);
    const totalEventCount         = ga4Total(4)??rawRows.reduce((s,r)=>s+getMetricValue(r,4),0);
    const totalActiveUsers        = ga4Total(5)??rawRows.reduce((s,r)=>s+getMetricValue(r,5),0);
    const totalKeyEvents          = ga4Total(6)??rawRows.reduce((s,r)=>s+getMetricValue(r,6),0);
    const userKeyEventRate        = totalActiveUsers > 0 ? totalKeyEvents / totalActiveUsers : 0;

    const cmpTotals=report?.compareTotals?.[0]?.metricValues||[];
    const cmpTotal=(idx)=>cmpTotals[idx]!=null?Number(cmpTotals[idx].value||0):0;
    const cmpTotalUsers        = cmpTotal(0);
    const cmpNewUsers          = cmpTotal(1);
    const cmpReturningUsers    = Math.max(0, cmpTotalUsers - cmpNewUsers);
    const cmpEngagementDuration= cmpTotal(2);
    const cmpEngagedSessions   = cmpTotal(3);
    const cmpEventCount        = cmpTotal(4);
    const cmpActiveUsers       = cmpTotal(5);
    const cmpKeyEvents         = cmpTotal(6);
    const cmpUserKeyEventRate  = cmpActiveUsers > 0 ? cmpKeyEvents / cmpActiveUsers : 0;

    return {
      totalTotalUsers, totalNewUsers, totalReturningUsers,
      totalEngagementDuration, totalEngagedSessions, totalEventCount, totalActiveUsers,
      totalKeyEvents, userKeyEventRate,
      avgEngagementTime:        totalActiveUsers>0?totalEngagementDuration/totalActiveUsers:0,
      engagedSessionsPerUser:   totalActiveUsers>0?totalEngagedSessions/totalActiveUsers:0,
      cmpTotalUsers, cmpNewUsers, cmpReturningUsers,
      cmpEngagementDuration, cmpEngagedSessions, cmpEventCount, cmpActiveUsers,
      cmpKeyEvents, cmpUserKeyEventRate,
      cmpAvgEngagementTime:     cmpActiveUsers>0?cmpEngagementDuration/cmpActiveUsers:0,
      cmpEngagedSessionsPerUser:cmpActiveUsers>0?cmpEngagedSessions/cmpActiveUsers:0,
    };
  }, [report,reportKey]);

  // ── Chart data ─────────────────────────────────────────────────────────────
  const chartData = useMemo(() => {
    if (reportKey === "user-acquisition") {
      const rawLabels = userAcqTimeseries?.labels || [];
      const series    = userAcqTimeseries?.series || [];
      const displayLabels = rawLabels.map(formatChartDateLabel);

      if (!rawLabels.length) {
        const topRows = (report?.rows||[]).slice(0,10);
        return {
          labels: topRows.map(r=>getDimensionValue(r)),
          datasets: [{ label:"Total Users", data:topRows.map(r=>getMetricValue(r,0)),
            backgroundColor:"#2b8ebf", borderRadius:8 }],
        };
      }
      const totalData = rawLabels.map((_,i)=>series.reduce((s,sr)=>s+Number(sr?.data?.[i]||0),0));
      return {
        labels: displayLabels,
        datasets: [
          { label:"Total", data:totalData, borderColor:"#2b8ebf",
            backgroundColor:"rgba(43,142,191,0.12)", fill:true, tension:0.4,
            borderWidth:2, borderDash:[6,3],
            pointRadius:displayLabels.length<=3?4:2, pointHoverRadius:5 },
          ...series.map((s,idx) => ({
            label:s.name, data:s.data,
            borderColor:PALETTE[idx%PALETTE.length],
            backgroundColor:`${PALETTE[idx%PALETTE.length]}22`,
            fill:false, tension:0.4, borderWidth:2,
            pointRadius:displayLabels.length<=3?4:2, pointHoverRadius:5,
          })),
        ],
      };
    }

    if (reportKey === "landing-pages") {
      const rawLabels = landingTimeseries?.labels || [];
      const series    = landingTimeseries?.series || [];
      const displayLabels = rawLabels.map(formatChartDateLabel);
      const totalData = rawLabels.map((_,i)=>series.reduce((s,sr)=>s+Number(sr?.data?.[i]||0),0));
      if (!rawLabels.length) {
        const topRows=(report?.rows||[]).slice(0,10);
        return { labels:topRows.map(r=>getDimensionValue(r)),
          datasets:[{ label:"Sessions", data:topRows.map(r=>getMetricValue(r,0)),
            borderColor:"#2b8ebf", backgroundColor:"rgba(43,142,191,0.15)",
            fill:true, tension:0.4, borderWidth:2, pointRadius:3, pointHoverRadius:5 }] };
      }
      return {
        labels: displayLabels,
        datasets: [
          { label:"Total", data:totalData, borderColor:"#2b8ebf",
            backgroundColor:"rgba(43,142,191,0.12)", fill:true, tension:0.4,
            borderWidth:2, borderDash:[6,3],
            pointRadius:displayLabels.length<=3?4:2, pointHoverRadius:5 },
          ...series.map((s,idx)=>({
            label:s.name, data:s.data,
            borderColor:PALETTE[idx%PALETTE.length],
            backgroundColor:`${PALETTE[idx%PALETTE.length]}22`,
            fill:false, tension:0.4, borderWidth:2,
            pointRadius:displayLabels.length<=3?4:2, pointHoverRadius:5,
          })),
        ],
      };
    }

    if (reportKey === "events") {
      const rawLabels = eventsTimeseries?.labels || [];
      const series    = eventsTimeseries?.series || [];
      const displayLabels = rawLabels.map(formatChartDateLabel);
      if (!rawLabels.length) {
        const topRows=(report?.rows||[]).slice(0,10);
        return { labels:topRows.map(r=>getDimensionValue(r)),
          datasets:[{ label:"Event Count", data:topRows.map(r=>getMetricValue(r,0)),
            borderColor:"#2b8ebf", backgroundColor:"rgba(43,142,191,0.15)",
            fill:true, tension:0.4, borderWidth:2, pointRadius:3, pointHoverRadius:5 }] };
      }
      const totalData = rawLabels.map((_,i)=>series.reduce((s,sr)=>s+Number(sr?.data?.[i]||0),0));
      return {
        labels: displayLabels,
        datasets: [
          { label:"Total", data:totalData, borderColor:"#2b8ebf",
            backgroundColor:"rgba(43,142,191,0.12)", fill:true, tension:0.4,
            borderWidth:2, borderDash:[6,3],
            pointRadius:displayLabels.length<=3?4:2, pointHoverRadius:5 },
          ...series.map((s,idx)=>({
            label:s.name, data:s.data,
            borderColor:PALETTE[idx%PALETTE.length],
            backgroundColor:`${PALETTE[idx%PALETTE.length]}22`,
            fill:false, tension:0.4, borderWidth:2,
            pointRadius:displayLabels.length<=3?4:2, pointHoverRadius:5,
          })),
        ],
      };
    }

    const topRows=(report?.rows||[]).slice(0,10);
    return {
      labels: topRows.map(r=>getDimensionValue(r)),
      datasets:[{ label:config.chartLabel, data:topRows.map(r=>getMetricValue(r,config.primaryMetricIndex)),
        backgroundColor:"#2b8ebf", borderRadius:8 }],
    };
  }, [report,config,reportKey,landingTimeseries,userAcqTimeseries,eventsTimeseries]);

  const chartOptions = useMemo(() => {
    const isLine = reportKey==="landing-pages"||reportKey==="user-acquisition"||reportKey==="events";
    const tsLength = reportKey==="landing-pages"?(landingTimeseries?.labels?.length||0)
      : reportKey==="user-acquisition"?(userAcqTimeseries?.labels?.length||0)
      : (eventsTimeseries?.labels?.length||0);
    const isSinglePoint = isLine && tsLength<=1;
    return {
      responsive:true, maintainAspectRatio:false,
      plugins: { legend:{display:true}, tooltip:{mode:"index",intersect:false} },
      scales: {
        x:{ ticks:{color:"#6b7280"}, grid:{display:false}, ...(isSinglePoint?{min:-0.5,max:0.5}:{}) },
        y:{ beginAtZero:true, ticks:{color:"#6b7280",precision:0}, grid:{color:"#eef2f7"} },
      },
      elements:{ line:{spanGaps:true} },
    };
  }, [reportKey,landingTimeseries,userAcqTimeseries,eventsTimeseries]);

  const selectedWebsite = websites.find(w=>String(w.key)===String(websiteKey));
  const optionLabel = (opt) => opt==="all"?`All (${totalRows.toLocaleString()})`:opt;
  const isLineReport = reportKey==="landing-pages"||reportKey==="user-acquisition"||reportKey==="events";

  const RowsPerPageSelect = ({ inline=false }) => (
    <select className={inline?"ms-2 me-2":"form-select"} value={rowLimit}
      onChange={e=>{ const v=e.target.value; setRowLimit(v==="all"?"all":Number(v)); }}>
      {rowLimitOptions.map(opt=>(
        <option key={opt} value={opt}>{optionLabel(opt)}</option>
      ))}
    </select>
  );

  const visibleCount = reportKey==="landing-pages" ? landingRows.length
    : reportKey==="user-acquisition" ? userAcqRows.length
    : eventsRows.length;

  const chartTitle = reportKey==="user-acquisition"
    ? "Total Users by Channel Group Over Time"
    : reportKey==="landing-pages"
    ? "Sessions by Landing Page Over Time"
    : `${config.title} chart`;

  return (
    <MDBContainer className="mt-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">{config.title}</h4>
          <div className="text-muted small">Detailed view for {selectedWebsite?.name||"selected website"}</div>
        </div>
        <Link to="/google-reports" className="btn btn-outline-secondary btn-sm">Back to summary</Link>
      </div>

      {/* ── Filters ── */}
      <MDBCard className="mb-4">
        <MDBCardBody>
          {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}
          <MDBRow className="row g-3 align-items-start">
            <MDBCol md="5">
              <label className="form-label">Website</label>
              <select className="form-select" value={websiteKey} onChange={e=>setWebsiteKey(e.target.value)}>
                <option value="">Select website</option>
                {websites.map(w=><option key={w.key} value={w.key}>{w.name||w.key}</option>)}
              </select>
            </MDBCol>
            <MDBCol md="5" style={{position:"relative"}}>
              <label className="form-label">Date Range</label>
              <div className="input-group">
                <input type="text" className="form-control" value={`${startDate} - ${endDate}`}
                  readOnly style={{cursor:"pointer"}} onClick={()=>setShowCalendar(!showCalendar)}/>
                <button className="btn btn-outline-secondary" type="button"
                  onClick={()=>setShowCalendar(!showCalendar)}>📅</button>
              </div>
              {showCalendar && (
                <CalendarPicker
                  startDate={startDate}
                  endDate={endDate}
                  onDateRangeSelect={(s, e) => {
                    setStartDate(s);
                    setEndDate(e);
                    setShowCalendar(false);
                  }}
                  onClose={() => setShowCalendar(false)}
                  isComparing={false}
                />
              )}
            </MDBCol>
            <MDBCol md="2" className="d-flex align-items-center gap-1">
              <MDBBtn className="text-sm px-2 btn-custom w-100" color="primary" onClick={fetchReport}>Apply</MDBBtn>
            </MDBCol>
          </MDBRow>
          <MDBRow className="g-3 mt-2">
            <MDBCol md="2" className="d-flex align-items-center gap-2">
              <input id="compareDetail" type="checkbox" checked={compareOn}
                onChange={e=>setCompareOn(e.target.checked)}/>
              <label htmlFor="compareDetail" className="mb-0">Compare</label>
            </MDBCol>
            {compareOn && (
              <Fragment>
                <MDBCol md="5" style={{position:"relative"}}>
                  <label className="form-label">Compare Date Range</label>
                  <div className="input-group">
                    <input type="text" className="form-control"
                      value={`${compareStartDate} - ${compareEndDate}`} readOnly style={{cursor:"pointer"}}
                      onClick={()=>setShowCompareCalendar(!showCompareCalendar)}/>
                    <button className="btn btn-outline-secondary" type="button"
                      onClick={()=>setShowCompareCalendar(!showCompareCalendar)}>📅</button>
                  </div>
                  {showCompareCalendar && (
                    <CalendarPicker startDate={compareStartDate} endDate={compareEndDate} onClose={() => setShowCompareCalendar(false)}
                      onDateRangeSelect={(s,e)=>{ setCompareStartDate(s); setCompareEndDate(e); setShowCompareCalendar(false); }}
                      isComparing={true}/>
                  )}
                </MDBCol>
              </Fragment>
            )}
            <MDBCol md="5">
              <label className="form-label">
                Rows per page
                {totalRows>0&&<span className="text-muted ms-1 small">({totalRows.toLocaleString()} total)</span>}
              </label>
              <RowsPerPageSelect/>
            </MDBCol>
          </MDBRow>
        </MDBCardBody>
      </MDBCard>

      {/* ── Summary cards ── */}
      <MDBRow className="g-4 mb-4">
        {reportKey==="user-acquisition" ? (
          <>
            <MDBCol md="4">
              <MDBCard><MDBCardBody>
                <div className="text-muted small">Total Users</div>
                <h3 className="mb-0">{formatNumber(userAcqSummary.totalTotalUsers)}</h3>
                {compareOn&&<div className="small mt-1">vs {formatNumber(userAcqSummary.cmpTotalUsers)}&nbsp;{renderPctChange(userAcqSummary.totalTotalUsers,userAcqSummary.cmpTotalUsers)}</div>}
              </MDBCardBody></MDBCard>
            </MDBCol>
            <MDBCol md="4">
              <MDBCard><MDBCardBody>
                <div className="text-muted small">New Users</div>
                <h3 className="mb-0">{formatNumber(userAcqSummary.totalNewUsers)}</h3>
                {compareOn&&<div className="small mt-1">vs {formatNumber(userAcqSummary.cmpNewUsers)}&nbsp;{renderPctChange(userAcqSummary.totalNewUsers,userAcqSummary.cmpNewUsers)}</div>}
              </MDBCardBody></MDBCard>
            </MDBCol>
            <MDBCol md="4">
              <MDBCard><MDBCardBody>
                <div className="text-muted small">Returning Users</div>
                <h3 className="mb-0">{formatNumber(userAcqSummary.totalReturningUsers)}</h3>
                {compareOn&&<div className="small mt-1">vs {formatNumber(userAcqSummary.cmpReturningUsers)}&nbsp;{renderPctChange(userAcqSummary.totalReturningUsers,userAcqSummary.cmpReturningUsers)}</div>}
              </MDBCardBody></MDBCard>
            </MDBCol>
          </>
        ) : (
          <>
            <MDBCol md="4">
              <MDBCard><MDBCardBody>
                <div className="text-muted small">{config.chartLabel}</div>
                <h3 className="mb-0">{formatNumber(summary.totalPrimary)}</h3>
                {compareOn&&<div className="small mt-1">vs {formatNumber(summary.cmpTotalSessions)}&nbsp;{renderPctChange(summary.totalPrimary,summary.cmpTotalSessions)}</div>}
              </MDBCardBody></MDBCard>
            </MDBCol>
            <MDBCol md="4">
              <MDBCard><MDBCardBody>
                <div className="text-muted small">{report?.metricHeaders?.[1]?.name||"Metric 2"}</div>
                <h3 className="mb-0">{formatNumber(summary.totalSecondary)}</h3>
                {compareOn&&<div className="small mt-1">vs {formatNumber(summary.cmpTotalActiveUsers)}&nbsp;{renderPctChange(summary.totalSecondary,summary.cmpTotalActiveUsers)}</div>}
              </MDBCardBody></MDBCard>
            </MDBCol>
            <MDBCol md="4">
              <MDBCard><MDBCardBody>
                <div className="text-muted small">{report?.metricHeaders?.[2]?.name||"Metric 3"}</div>
                <h3 className="mb-0">{formatNumber(summary.totalThird)}</h3>
                {compareOn&&<div className="small mt-1">vs {formatNumber(summary.cmpTotalNewUsers)}&nbsp;{renderPctChange(summary.totalThird,summary.cmpTotalNewUsers)}</div>}
              </MDBCardBody></MDBCard>
            </MDBCol>
          </>
        )}
      </MDBRow>

      {/* ── Chart ── */}
      <MDBCard className="mb-4">
        <MDBCardHeader><h5 className="mb-0">{chartTitle}</h5></MDBCardHeader>
        <MDBCardBody>
          <div style={{height:"350px"}}>
            {isLineReport
              ? <Line data={chartData} options={chartOptions}/>
              : <Bar  data={chartData} options={chartOptions}/>}
          </div>
        </MDBCardBody>
      </MDBCard>

      {/* ── Table ── */}
      <MDBCard className="mb-4">
        <MDBCardHeader><h5 className="mb-0">{config.title} table</h5></MDBCardHeader>
        <MDBCardBody>
          <div className="table-responsive">

            {/* ── Search toolbar: always visible when data exists ── */}
            {isLineReport && totalRows > 0 && (
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center gap-2">
                  <button type="button" className="btn btn-light btn-sm border" disabled>Plot rows</button>
                  <div style={{minWidth:"260px"}}>
                    <MDBInput
                      type="text"
                      size="sm"
                      placeholder="Search..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </div>
                  {search && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setSearch("")}
                    >
                      ✕ Clear
                    </button>
                  )}
                </div>
                <div className="text-muted small d-flex align-items-center">
                  Rows per page:<RowsPerPageSelect inline/>| 1–{visibleCount} of {totalRows.toLocaleString()}
                </div>
              </div>
            )}

            {/* ── No data at all ── */}
            {totalRows === 0 ? (
              <div className="text-muted py-3 text-center">No data available.</div>

            /* ── Search returned no results ── */
            ) : visibleCount === 0 ? (
              <div className="py-3 text-center">
                <div className="text-muted">No results match <strong>"{search}"</strong>.</div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary mt-2"
                  onClick={() => setSearch("")}
                >
                  Clear search
                </button>
              </div>

            /* ── Data table ── */
            ) : (
              <>
                <table className="table table-striped table-sm">
                  <thead>
                    <tr>
                      {reportKey==="user-acquisition" ? (
                        <>
                          <th>#</th>
                          <th role="button" onClick={()=>handleSort("channelGroup")}>First User Channel Group</th>
                          <th role="button" onClick={()=>handleSort("totalUsers")}>Total Users</th>
                          <th role="button" onClick={()=>handleSort("newUsers")}>New Users</th>
                          <th role="button" onClick={()=>handleSort("returningUsers")}>Returning Users</th>
                          <th role="button" onClick={()=>handleSort("userEngagementDuration")}>Avg engagement time per active user</th>
                          <th role="button" onClick={()=>handleSort("engagedSessions")}>Engaged sessions per active user</th>
                          <th role="button" onClick={()=>handleSort("eventCount")}>Event count</th>
                          <th role="button" onClick={()=>handleSort("keyEvents")}>Key events</th>
                          <th role="button" onClick={()=>handleSort("userKeyEventRate")}>User key event rate</th>
                        </>
                      ) : reportKey==="landing-pages" ? (
                        <>
                          <th>#</th>
                          <th role="button" onClick={()=>handleSort("landingPage")}>Landing Page</th>
                          <th role="button" onClick={()=>handleSort("sessions")}>Sessions</th>
                          <th role="button" onClick={()=>handleSort("activeUsers")}>Active Users</th>
                          <th role="button" onClick={()=>handleSort("newUsers")}>New Users</th>
                          <th role="button" onClick={()=>handleSort("userEngagementDuration")}>Avg engagement time per session</th>
                          <th role="button" onClick={()=>handleSort("keyEvents")}>Key events</th>
                          <th role="button" onClick={()=>handleSort("totalRevenue")}>Total revenue</th>
                          <th role="button" onClick={()=>handleSort("sessionKeyEventRate")}>Session key event rate</th>
                        </>
                      ) : (
                        <>
                          <th>#</th>
                          <th role="button" onClick={()=>handleSort("eventName")}>Event name</th>
                          <th role="button" onClick={()=>handleSort("eventCount")}>Event count</th>
                          <th role="button" onClick={()=>handleSort("totalUsers")}>Total users</th>
                          <th role="button" onClick={()=>handleSort("eventCountPerUser")}>Event count per active user</th>
                          <th role="button" onClick={()=>handleSort("totalRevenue")}>Total revenue</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>

                    {/* ── User Acquisition table ── */}
                    {reportKey==="user-acquisition" && (
                      <>
                        <tr className="table-light fw-bold">
                          <td>-</td>
                          <td>Total</td>
                          <td>
                            {formatNumber(userAcqSummary.totalTotalUsers)}
                            <div className="text-muted small fw-normal">100% of total</div>
                            {compareOn&&<><div className="text-muted small fw-normal">{formatNumber(userAcqSummary.cmpTotalUsers)}</div><div className="small">{renderPctChange(userAcqSummary.totalTotalUsers,userAcqSummary.cmpTotalUsers)}</div></>}
                          </td>
                          <td>
                            {formatNumber(userAcqSummary.totalNewUsers)}
                            <div className="text-muted small fw-normal">100% of total</div>
                            {compareOn&&<><div className="text-muted small fw-normal">{formatNumber(userAcqSummary.cmpNewUsers)}</div><div className="small">{renderPctChange(userAcqSummary.totalNewUsers,userAcqSummary.cmpNewUsers)}</div></>}
                          </td>
                          <td>
                            {formatNumber(userAcqSummary.totalReturningUsers)}
                            <div className="text-muted small fw-normal">100% of total</div>
                            {compareOn&&<><div className="text-muted small fw-normal">{formatNumber(userAcqSummary.cmpReturningUsers)}</div><div className="small">{renderPctChange(userAcqSummary.totalReturningUsers,userAcqSummary.cmpReturningUsers)}</div></>}
                          </td>
                          <td>
                            {formatDurationSeconds(userAcqSummary.avgEngagementTime)}
                            <div className="text-muted small fw-normal">Avg</div>
                            {compareOn&&<><div className="text-muted small fw-normal">{formatDurationSeconds(userAcqSummary.cmpAvgEngagementTime)}</div><div className="small">{renderPctChange(userAcqSummary.avgEngagementTime,userAcqSummary.cmpAvgEngagementTime)}</div></>}
                          </td>
                          <td>
                            {formatDecimal(userAcqSummary.engagedSessionsPerUser)}
                            <div className="text-muted small fw-normal">Avg</div>
                            {compareOn&&<><div className="text-muted small fw-normal">{formatDecimal(userAcqSummary.cmpEngagedSessionsPerUser)}</div><div className="small">{renderPctChange(userAcqSummary.engagedSessionsPerUser,userAcqSummary.cmpEngagedSessionsPerUser)}</div></>}
                          </td>
                          <td>
                            {formatNumber(userAcqSummary.totalEventCount)}
                            <div className="text-muted small fw-normal">100% of total</div>
                            {compareOn&&<><div className="text-muted small fw-normal">{formatNumber(userAcqSummary.cmpEventCount)}</div><div className="small">{renderPctChange(userAcqSummary.totalEventCount,userAcqSummary.cmpEventCount)}</div></>}
                          </td>
                          <td>
                            {formatNumber(userAcqSummary.totalKeyEvents)}
                            <div className="text-muted small fw-normal">100% of total</div>
                            {compareOn&&<><div className="text-muted small fw-normal">{formatNumber(userAcqSummary.cmpKeyEvents)}</div><div className="small">{renderPctChange(userAcqSummary.totalKeyEvents,userAcqSummary.cmpKeyEvents)}</div></>}
                          </td>
                          <td>
                            {formatPercent(userAcqSummary.userKeyEventRate)}
                            <div className="text-muted small fw-normal">Avg</div>
                            {compareOn&&<><div className="text-muted small fw-normal">{formatPercent(userAcqSummary.cmpUserKeyEventRate)}</div><div className="small">{renderPctChange(userAcqSummary.userKeyEventRate,userAcqSummary.cmpUserKeyEventRate)}</div></>}
                          </td>
                        </tr>

                        {userAcqRows.map((row,idx) => {
                          const avgEng        = row.activeUsers>0?row.userEngagementDuration/row.activeUsers:0;
                          const engPerUser    = row.activeUsers>0?row.engagedSessions/row.activeUsers:0;
                          const cmpAvgEng     = row.cmpActiveUsers>0?row.cmpUserEngagementDuration/row.cmpActiveUsers:0;
                          const cmpEngPerUser = row.cmpActiveUsers>0?row.cmpEngagedSessions/row.cmpActiveUsers:0;
                          return (
                            <Fragment key={`${row.channelGroup}_${idx}`}>
                              <tr>
                                <td>{idx+1}</td>
                                <td>{row.channelGroup}</td>
                                <td>
                                  {formatNumber(row.totalUsers)}
                                  <div className="text-muted small">{formatPercent(userAcqSummary.totalTotalUsers?row.totalUsers/userAcqSummary.totalTotalUsers:0)}</div>
                                </td>
                                <td>
                                  {formatNumber(row.newUsers)}
                                  <div className="text-muted small">{formatPercent(userAcqSummary.totalNewUsers?row.newUsers/userAcqSummary.totalNewUsers:0)}</div>
                                </td>
                                <td>
                                  {formatNumber(row.returningUsers)}
                                  <div className="text-muted small">{formatPercent(userAcqSummary.totalReturningUsers?row.returningUsers/userAcqSummary.totalReturningUsers:0)}</div>
                                </td>
                                <td>{formatDurationSeconds(avgEng)}</td>
                                <td>{formatDecimal(engPerUser)}</td>
                                <td>
                                  {formatNumber(row.eventCount)}
                                  <div className="text-muted small">{formatPercent(userAcqSummary.totalEventCount?row.eventCount/userAcqSummary.totalEventCount:0)}</div>
                                </td>
                                <td>
                                  {formatNumber(row.keyEvents)}
                                  <div className="text-muted small">{formatPercent(userAcqSummary.totalKeyEvents?row.keyEvents/userAcqSummary.totalKeyEvents:0)}</div>
                                </td>
                                <td>{formatPercent(row.userKeyEventRate)}</td>
                              </tr>
                              {compareOn && (
                                <>
                                  <tr style={{backgroundColor:"#f9fafb",fontSize:"13px"}}>
                                    <td></td>
                                    <td style={{color:"#6b7280"}}>{compareStartDate} – {compareEndDate}</td>
                                    <td>
                                      {formatNumber(row.cmpTotalUsers)}
                                      <div className="text-muted small">{formatPercent(userAcqSummary.cmpTotalUsers?row.cmpTotalUsers/userAcqSummary.cmpTotalUsers:0)}</div>
                                    </td>
                                    <td>
                                      {formatNumber(row.cmpNewUsers)}
                                      <div className="text-muted small">{formatPercent(userAcqSummary.cmpNewUsers?row.cmpNewUsers/userAcqSummary.cmpNewUsers:0)}</div>
                                    </td>
                                    <td>
                                      {formatNumber(row.cmpReturningUsers)}
                                      <div className="text-muted small">{formatPercent(userAcqSummary.cmpReturningUsers?row.cmpReturningUsers/userAcqSummary.cmpReturningUsers:0)}</div>
                                    </td>
                                    <td>{formatDurationSeconds(cmpAvgEng)}</td>
                                    <td>{formatDecimal(cmpEngPerUser)}</td>
                                    <td>
                                      {formatNumber(row.cmpEventCount)}
                                      <div className="text-muted small">{formatPercent(userAcqSummary.cmpEventCount?row.cmpEventCount/userAcqSummary.cmpEventCount:0)}</div>
                                    </td>
                                    <td>
                                      {formatNumber(row.cmpKeyEvents)}
                                      <div className="text-muted small">{formatPercent(userAcqSummary.cmpKeyEvents?row.cmpKeyEvents/userAcqSummary.cmpKeyEvents:0)}</div>
                                    </td>
                                    <td>{formatPercent(row.cmpUserKeyEventRate)}</td>
                                  </tr>
                                  <tr style={{backgroundColor:"#f0f9ff",fontSize:"12px"}}>
                                    <td></td>
                                    <td style={{color:"#1f2937",fontWeight:600}}>% change</td>
                                    <td>{renderPctChange(row.totalUsers,row.cmpTotalUsers)}</td>
                                    <td>{renderPctChange(row.newUsers,row.cmpNewUsers)}</td>
                                    <td>{renderPctChange(row.returningUsers,row.cmpReturningUsers)}</td>
                                    <td>{renderPctChange(avgEng,cmpAvgEng)}</td>
                                    <td>{renderPctChange(engPerUser,cmpEngPerUser)}</td>
                                    <td>{renderPctChange(row.eventCount,row.cmpEventCount)}</td>
                                    <td>{renderPctChange(row.keyEvents,row.cmpKeyEvents)}</td>
                                    <td>{renderPctChange(row.userKeyEventRate,row.cmpUserKeyEventRate)}</td>
                                  </tr>
                                </>
                              )}
                            </Fragment>
                          );
                        })}
                      </>
                    )}

                    {/* ── Landing Pages table ── */}
                    {reportKey==="landing-pages" && (
                      <>
                        <tr className="table-light fw-bold">
                          <td>-</td><td>Total</td>
                          <td>{formatNumber(summary.totalPrimary)}<div className="text-muted small fw-normal">100% of total</div>{compareOn&&<><div className="text-muted small fw-normal">{formatNumber(summary.cmpTotalSessions)}</div><div className="small">{renderPctChange(summary.totalPrimary,summary.cmpTotalSessions)}</div></>}</td>
                          <td>{formatNumber(summary.totalSecondary)}<div className="text-muted small fw-normal">100% of total</div>{compareOn&&<><div className="text-muted small fw-normal">{formatNumber(summary.cmpTotalActiveUsers)}</div><div className="small">{renderPctChange(summary.totalSecondary,summary.cmpTotalActiveUsers)}</div></>}</td>
                          <td>{formatNumber(summary.totalThird)}<div className="text-muted small fw-normal">100% of total</div>{compareOn&&<><div className="text-muted small fw-normal">{formatNumber(summary.cmpTotalNewUsers)}</div><div className="small">{renderPctChange(summary.totalThird,summary.cmpTotalNewUsers)}</div></>}</td>
                          <td>{formatDurationSeconds(summary.totalFourth)}<div className="text-muted small fw-normal">Avg</div>{compareOn&&<><div className="text-muted small fw-normal">{formatDurationSeconds(summary.cmpTotalAvgSessionDuration)}</div><div className="small">{renderPctChange(summary.totalFourth,summary.cmpTotalAvgSessionDuration)}</div></>}</td>
                          <td>{formatNumber(summary.totalFifth)}<div className="text-muted small fw-normal">100% of total</div>{compareOn&&<><div className="text-muted small fw-normal">{formatNumber(summary.cmpTotalKeyEvents)}</div><div className="small">{renderPctChange(summary.totalFifth,summary.cmpTotalKeyEvents)}</div></>}</td>
                          <td>${formatNumber(summary.totalSixth.toFixed?summary.totalSixth.toFixed(2):summary.totalSixth)}<div className="text-muted small fw-normal">100% of total</div>{compareOn&&<><div className="text-muted small fw-normal">${formatNumber(summary.cmpTotalRevenue.toFixed?summary.cmpTotalRevenue.toFixed(2):summary.cmpTotalRevenue)}</div><div className="small">{renderPctChange(summary.totalSixth,summary.cmpTotalRevenue)}</div></>}</td>
                          <td>{formatPercent(summary.totalSeventh)}{compareOn&&<><div className="text-muted small fw-normal">{formatPercent(summary.cmpTotalSessionKeyEventRate)}</div><div className="small">{renderPctChange(summary.totalSeventh,summary.cmpTotalSessionKeyEventRate)}</div></>}</td>
                        </tr>
                        {landingRows.map((row,idx) => {
                          const rowAvgDuration=row.sessions>0?row.userEngagementDuration/row.sessions:0;
                          const cmpAvgDuration=row.cmpSessions>0?row.cmpUserEngagementDuration/row.cmpSessions:0;
                          return (
                            <Fragment key={`${row.landingPage}_${idx}`}>
                              <tr>
                                <td>{idx+1}</td><td>{row.landingPage}</td>
                                <td>{formatNumber(row.sessions)}<div className="text-muted small">{formatPercent(summary.totalPrimary?row.sessions/summary.totalPrimary:0)}</div></td>
                                <td>{formatNumber(row.activeUsers)}<div className="text-muted small">{formatPercent(summary.totalSecondary?row.activeUsers/summary.totalSecondary:0)}</div></td>
                                <td>{formatNumber(row.newUsers)}<div className="text-muted small">{formatPercent(summary.totalThird?row.newUsers/summary.totalThird:0)}</div></td>
                                <td>{formatDurationSeconds(rowAvgDuration)}</td>
                                <td>{formatNumber(row.keyEvents)}</td>
                                <td>${formatNumber(row.totalRevenue.toFixed?row.totalRevenue.toFixed(2):row.totalRevenue)}</td>
                                <td>{formatPercent(row.sessionKeyEventRate)}</td>
                              </tr>
                              {compareOn&&(
                                <>
                                  <tr style={{backgroundColor:"#f9fafb",fontSize:"13px"}}>
                                    <td></td><td style={{color:"#6b7280"}}>{compareStartDate} – {compareEndDate}</td>
                                    <td>{formatNumber(row.cmpSessions)}<div className="text-muted small">{formatPercent(summary.cmpTotalSessions?row.cmpSessions/summary.cmpTotalSessions:0)}</div></td>
                                    <td>{formatNumber(row.cmpActiveUsers)}<div className="text-muted small">{formatPercent(summary.cmpTotalActiveUsers?row.cmpActiveUsers/summary.cmpTotalActiveUsers:0)}</div></td>
                                    <td>{formatNumber(row.cmpNewUsers)}<div className="text-muted small">{formatPercent(summary.cmpTotalNewUsers?row.cmpNewUsers/summary.cmpTotalNewUsers:0)}</div></td>
                                    <td>{formatDurationSeconds(cmpAvgDuration)}</td>
                                    <td>{formatNumber(row.cmpKeyEvents)}</td>
                                    <td>${formatNumber(row.cmpTotalRevenue.toFixed?row.cmpTotalRevenue.toFixed(2):row.cmpTotalRevenue)}</td>
                                    <td>{formatPercent(row.cmpSessionKeyEventRate)}</td>
                                  </tr>
                                  <tr style={{backgroundColor:"#f0f9ff",fontSize:"12px"}}>
                                    <td></td><td style={{color:"#1f2937",fontWeight:600}}>% change</td>
                                    <td>{renderPctChange(row.sessions,row.cmpSessions)}</td>
                                    <td>{renderPctChange(row.activeUsers,row.cmpActiveUsers)}</td>
                                    <td>{renderPctChange(row.newUsers,row.cmpNewUsers)}</td>
                                    <td>{renderPctChange(rowAvgDuration,cmpAvgDuration)}</td>
                                    <td>{renderPctChange(row.keyEvents,row.cmpKeyEvents)}</td>
                                    <td>{renderPctChange(row.totalRevenue,row.cmpTotalRevenue)}</td>
                                    <td>{renderPctChange(row.sessionKeyEventRate,row.cmpSessionKeyEventRate)}</td>
                                  </tr>
                                </>
                              )}
                            </Fragment>
                          );
                        })}
                      </>
                    )}

                    {/* ── Events table ── */}
                    {reportKey==="events" && (
                      <>
                        <tr className="table-light fw-bold">
                          <td>-</td><td>Total</td>
                          <td>
                            {formatNumber(eventsSummary.totalEventCount)}
                            <div className="text-muted small fw-normal">100% of total</div>
                            {compareOn&&<><div className="text-muted small fw-normal">{formatNumber(eventsSummary.cmpEventCount)}</div><div className="small">{renderPctChange(eventsSummary.totalEventCount,eventsSummary.cmpEventCount)}</div></>}
                          </td>
                          <td>
                            {formatNumber(eventsSummary.totalTotalUsers)}
                            <div className="text-muted small fw-normal">100% of total</div>
                            {compareOn&&<><div className="text-muted small fw-normal">{formatNumber(eventsSummary.cmpTotalUsers)}</div><div className="small">{renderPctChange(eventsSummary.totalTotalUsers,eventsSummary.cmpTotalUsers)}</div></>}
                          </td>
                          <td>
                            {formatDecimal(eventsSummary.avgEventCountPerUser)}
                            <div className="text-muted small fw-normal">Avg</div>
                            {compareOn&&<><div className="text-muted small fw-normal">{formatDecimal(eventsSummary.cmpEventCountPerUser)}</div><div className="small">{renderPctChange(eventsSummary.avgEventCountPerUser,eventsSummary.cmpEventCountPerUser)}</div></>}
                          </td>
                          <td>
                            ${formatNumber(Number(eventsSummary.totalRevenue||0).toFixed(2))}
                            <div className="text-muted small fw-normal">100% of total</div>
                            {compareOn&&<><div className="text-muted small fw-normal">${formatNumber(Number(eventsSummary.cmpTotalRevenue||0).toFixed(2))}</div><div className="small">{renderPctChange(eventsSummary.totalRevenue,eventsSummary.cmpTotalRevenue)}</div></>}
                          </td>
                        </tr>
                        {eventsRows.map((row,idx) => (
                          <Fragment key={`${row.eventName}_${idx}`}>
                            <tr>
                              <td>{idx+1}</td>
                              <td>{row.eventName}</td>
                              <td>
                                {formatNumber(row.eventCount)}
                                <div className="text-muted small">{formatPercent(eventsSummary.totalEventCount?row.eventCount/eventsSummary.totalEventCount:0)}</div>
                              </td>
                              <td>
                                {formatNumber(row.totalUsers)}
                                <div className="text-muted small">{formatPercent(eventsSummary.totalTotalUsers?row.totalUsers/eventsSummary.totalTotalUsers:0)}</div>
                              </td>
                              <td>{formatDecimal(row.eventCountPerUser)}</td>
                              <td>${formatNumber(Number(row.totalRevenue||0).toFixed(2))}</td>
                            </tr>
                            {compareOn&&(
                              <>
                                <tr style={{backgroundColor:"#f9fafb",fontSize:"13px"}}>
                                  <td></td>
                                  <td style={{color:"#6b7280"}}>{compareStartDate} – {compareEndDate}</td>
                                  <td>
                                    {formatNumber(row.cmpEventCount)}
                                    <div className="text-muted small">{formatPercent(eventsSummary.cmpEventCount?row.cmpEventCount/eventsSummary.cmpEventCount:0)}</div>
                                  </td>
                                  <td>
                                    {formatNumber(row.cmpTotalUsers)}
                                    <div className="text-muted small">{formatPercent(eventsSummary.cmpTotalUsers?row.cmpTotalUsers/eventsSummary.cmpTotalUsers:0)}</div>
                                  </td>
                                  <td>{formatDecimal(row.cmpEventCountPerUser)}</td>
                                  <td>${formatNumber(Number(row.cmpTotalRevenue||0).toFixed(2))}</td>
                                </tr>
                                <tr style={{backgroundColor:"#f0f9ff",fontSize:"12px"}}>
                                  <td></td>
                                  <td style={{color:"#1f2937",fontWeight:600}}>% change</td>
                                  <td>{renderPctChange(row.eventCount,row.cmpEventCount)}</td>
                                  <td>{renderPctChange(row.totalUsers,row.cmpTotalUsers)}</td>
                                  <td>{renderPctChange(row.eventCountPerUser,row.cmpEventCountPerUser)}</td>
                                  <td>{renderPctChange(row.totalRevenue,row.cmpTotalRevenue)}</td>
                                </tr>
                              </>
                            )}
                          </Fragment>
                        ))}
                      </>
                    )}

                  </tbody>
                </table>
                <div className="text-muted small text-end mt-2">
                  Showing {visibleCount.toLocaleString()} of {totalRows.toLocaleString()} rows
                </div>
              </>
            )}
          </div>
        </MDBCardBody>
      </MDBCard>
    </MDBContainer>
  );
};

export default connect(null, mapDispatchToProps)(GoogleReportDetailPage);